import { faCheck, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@material-ui/core/Box';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Option, PetType } from '@spike/model';
import { updateStaffServicesThunk } from 'actions/services/ServicesActions';
import ConfirmDialog from 'components/UI/ConfirmDialog';
import { Grid } from 'components/UI/Grid';
import { GridElement } from 'components/UI/Grid/Grid';
import MessageDialog from 'components/UI/MessageDialog';
import { SelectionBar } from 'components/UI/SelectionBar';
import Spinner from 'components/UI/Spinner';
import { useMasterData } from 'hooks';
import { SelectableOption, Status } from 'model';
import Service, { Meta, SortFields, SortOrder, StaffMember } from 'model/Service';
import { Staff } from 'model/Staff';
import { Fragment, FunctionComponent, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { RootState } from 'store';
import { ResultNotFound } from './ResultsNotFound';
import ServicesHeader from './ServicesHeader';
import { ServicesHeaderList } from './ServicesHeaderList';
import ServicesMultiselectionBar from './ServicesMultiselectionBar';
import { ServicesNotCreated } from './ServicesNotCreated';
import ServicesRowContent from './ServicesRowContent';
import { useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';
import Search from './Search';
import { Pagination } from 'components/UI';

interface Props {
    businessAreas: Array<Option<string>>;
    petTypes: Array<PetType>;
    duplicatedServiceId?: number;
    save: (service: Service, duplicated?: boolean) => void;
    delete: (serviceIds: Array<number>) => void;
    activate: (serviceIds: Array<number>, active: boolean) => void;
    updatePetTypes: (serviceIds: Array<number>, petTypeIds: Array<string>) => void;
    updateBusinessArea: (serviceIds: Array<number>, businessAreaId: string) => void;
    addService: () => void;
    editService: (service: Service) => void;
    addStaff: (service: Service) => void;
    // Props for data control (following DaycareServicesTable pattern)
    services: Array<Service>;
    meta: Meta | null;
    loading: boolean;
    hasInitialized: boolean;
    isTrulyEmpty: boolean;
    hasEverHadServices: boolean;
    onFetchServices: (options?: {
        page?: number;
        status?: string;
        search?: string;
        pageSize?: number;
        marketplaceIds?: Array<number>;
    }) => void;
    selectedMarketplaceIds: Array<number>;
    onChangeMarketplaceIds: (marketplaceIds: Array<number>) => void;
}

interface Filter {
    status: string;
    searchText: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            background: 'white',
            height: '100%'
        },
        headerContainer: {
            display: 'flex',
            width: '100%',
            [theme.breakpoints.only('lg')]: {
                marginTop: 45
            },
            [theme.breakpoints.down('xl')]: {
                marginTop: 45
            },
            [theme.breakpoints.down('md')]: {
                marginTop: 30
            }
        },
        selectorsContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            [theme.breakpoints.down('sm')]: {
                marginTop: 26
            },
            marginTop: 30,
            [theme.breakpoints.down('md')]: {
                paddingLeft: 15,
                paddingRight: 15
            }
        },
        selectorContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            border: '1px solid red',
            [theme.breakpoints.down('sm')]: {
                width: 94,
                paddingRight: 10
            },
            [theme.breakpoints.only('md')]: {
                width: 126,
                paddingRight: 15
            },
            [theme.breakpoints.only('lg')]: {
                width: 168,
                paddingRight: 20
            },
            [theme.breakpoints.up('xl')]: {
                width: 224,
                paddingRight: 40
            }
        },
        selectorBarContainer: {
            display: 'flex',
            paddingLeft: 25,
            [theme.breakpoints.down('md')]: {
                paddingLeft: 0
            }
        },
        gridContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            [theme.breakpoints.up('md')]: {
                marginTop: 58
            },
            [theme.breakpoints.down('md')]: {
                marginTop: 20
            }
        },
        deleteConfirmTextContainer: {
            width: '100%'
        },
        deleteConfirmText: {
            width: '100%',
            textAlign: 'center',
            [theme.breakpoints.down('lg')]: {
                fontSize: 15
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: 15
            }
        },
        deleteConfirmTextTitle: {
            display: 'none',
            [theme.breakpoints.down('md')]: {
                display: 'block',
                fontSize: 24,
                fontWeight: 600,
                marginBottom: 10
            }
        },
        deleteConfirmServices: {
            width: '100%',
            textAlign: 'left',
            [theme.breakpoints.down('lg')]: {
                fontSize: 15,
                marginTop: 17
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: 15,
                marginTop: 23
            }
        },
        messageDialog: {
            width: '100%',
            textAlign: 'left',
            [theme.breakpoints.down('lg')]: {
                fontSize: 15
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: 15
            }
        },
        deleteIcon: {
            [theme.breakpoints.down('lg')]: {
                marginLeft: 16,
                marginRight: 16
            },
            [theme.breakpoints.up('xl')]: {
                marginLeft: 22,
                marginRight: 22
            }
        },
        spinner: {
            marginTop: 20
        },
        servicesNotCreatedContainer: {
            [theme.breakpoints.down('lg')]: {
                marginTop: 96
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: 128
            }
        },
        grid: {
            '& .makeStyles-toolbarButtonContainer-249': {
                alignItems: 'start !important',
                [theme.breakpoints.down('lg')]: {
                    marginTop: 8
                },
                [theme.breakpoints.up('xl')]: {
                    marginTop: 12
                }
            }
        },
        gridServices: {
            [theme.breakpoints.down('md')]: {
                '& > div > div:first-child': {
                    display: 'none'
                },
                '& > div > div:nth-child(2)': {
                    width: '100%'
                }
            }
        },
        selectorBarBlock: {
            backgroundColor: '#FFFFFF'
        },
        actionsMobile: {
            position: 'relative'
        },
        pagination: {
            paddingRight: 20,
            [theme.breakpoints.down('md')]: {
                paddingRight: 12
            }
        }
    })
);

export const ServicesTable: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const masterData = useMasterData();

    const petTypesDuplicateOption: SelectableOption<string> = {
        element: { id: 'pet_type', name: 'Pet Type' },
        selected: true
    };
    const descriptionDuplicateOption: SelectableOption<string> = {
        element: { id: 'services_description', name: 'Services Description' },
        selected: false
    };
    const staffDuplicateOption: SelectableOption<string> = {
        element: { id: 'staff', name: 'Members' },
        selected: false
    };
    const priceDuplicateOption: SelectableOption<string> = {
        element: { id: 'price', name: 'Pricing' },
        selected: false
    };
    const durationDuplicateOption: SelectableOption<string> = {
        element: { id: 'services_duration', name: 'Services Duration' },
        selected: false
    };
    const tagsDuplicateOption: SelectableOption<string> = {
        element: { id: 'services_tags', name: 'Services Tags' },
        selected: false
    };
    const duplicateOptions: Array<SelectableOption<string>> = [
        petTypesDuplicateOption,
        descriptionDuplicateOption,
        staffDuplicateOption,
        priceDuplicateOption,
        durationDuplicateOption,
        tagsDuplicateOption
    ];

    const activeOption: Option<string> = { id: 'active', name: 'Active' };
    const inactiveOption: Option<string> = { id: 'inactive', name: 'Inactive' };
    const allOption: Option<string> = { id: 'all', name: 'All' };

    // Data controlled by props (from useGroomingServices hook)
    const {
        services,
        meta,
        loading,
        hasInitialized,
        isTrulyEmpty,
        hasEverHadServices,
        onFetchServices,
        selectedMarketplaceIds,
        onChangeMarketplaceIds
    } = props;
    const status = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const staff: Array<StaffMember> = useSelector<RootState, Array<Staff>>(state => state.staff.staff).map(staff => ({
        id: staff.id!,
        firstName: staff.person.firstName,
        lastName: staff.person.lastName,
        avatar: staff.person.avatar
    }));

    const [field, setField] = useState<SortFields>(SortFields.NAME);
    const [order, setOrder] = useState<SortOrder>(SortOrder.ASCENDANT);
    const [selected, setSelected] = useState<Array<number>>([]);
    const [selectedQty, setSelectedQty] = useState(0);
    const [showMultiselectionBar, setShowMultiselectionBar] = useState(false);

    const [filter, setFilter] = useState<Filter>({
        status: activeOption.id,
        searchText: ''
    });

    const [filterOptions] = useState<Array<Option<string>>>([activeOption, inactiveOption, allOption]);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Hybrid loading state (similar to DaycareComponent)
    const [localLoading, setLocalLoading] = useState({
        services: false, // For filter/pagination changes
        initialLoading: false // For initial load
    });

    // Track if we've made our first filter-based fetch to prevent loops
    const hasFirstFilterFetch = useRef(false);
    // Store the fetch function ref to avoid dependency issues
    const onFetchServicesRef = useRef(onFetchServices);
    // Store previous filter values to detect real changes
    const prevFiltersRef = useRef({
        page,
        pageSize,
        status: filter.status,
        search: filter.searchText,
        marketplaceIds: selectedMarketplaceIds
    });

    // Update ref when onFetchServices changes
    useEffect(() => {
        onFetchServicesRef.current = onFetchServices;
    }, [onFetchServices]);

    // Mark as ready for filter-based fetches once initialized
    useEffect(() => {
        if (hasInitialized && !hasFirstFilterFetch.current) {
            hasFirstFilterFetch.current = true;
        }
    }, [hasInitialized]);

    // Fetch when filters/pagination change (but NOT on first render after initialization)
    useEffect(() => {
        const currentFilters = {
            page,
            pageSize,
            status: filter.status,
            search: filter.searchText,
            marketplaceIds: selectedMarketplaceIds
        };
        const prevFilters = prevFiltersRef.current;

        // Check if filters actually changed
        const marketplaceIdsChanged =
            currentFilters.marketplaceIds.length !== prevFilters.marketplaceIds.length ||
            currentFilters.marketplaceIds.some((id, index) => id !== prevFilters.marketplaceIds[index]);

        const filtersChanged =
            currentFilters.page !== prevFilters.page ||
            currentFilters.pageSize !== prevFilters.pageSize ||
            currentFilters.status !== prevFilters.status ||
            currentFilters.search !== prevFilters.search ||
            marketplaceIdsChanged;

        // Only fetch if:
        // 1. We've initialized
        // 2. We're ready for filter-based fetches
        // 3. Filters actually changed (not just a re-render)
        if (hasInitialized && hasFirstFilterFetch.current && filtersChanged) {
            // Set loading state before fetch to prevent ServicesNotCreated flashing
            setLocalLoading(prev => ({ ...prev, services: true }));
            onFetchServicesRef.current(currentFilters);
        }

        // Update previous filters
        prevFiltersRef.current = currentFilters;
    }, [page, pageSize, filter.status, filter.searchText, selectedMarketplaceIds, hasInitialized]);

    // Reset local loading when hook loading completes
    useEffect(() => {
        if (!loading) {
            setLocalLoading(prev => ({ ...prev, services: false }));
        }
    }, [loading]);

    // Refetch after operations are successful
    useEffect(() => {
        if (
            status === ServicesStatus.DeleteSuccess ||
            status === ServicesStatus.ActivateSuccess ||
            status === ServicesStatus.InactivateSuccess
        ) {
            // Set loading state before refresh to show loading
            setLocalLoading(prev => ({ ...prev, services: true }));
            // Refresh with current filters after operations
            onFetchServicesRef.current({
                page,
                pageSize,
                status: filter.status,
                search: filter.searchText || '',
                marketplaceIds: selectedMarketplaceIds
            });
        }
    }, [status, page, pageSize, filter.status, filter.searchText, selectedMarketplaceIds]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let show = false;
        if (status === ServicesStatus.DeleteSuccess) {
            setMessageDialog(deleteMessage);
            show = true;
        }
        if (status === ServicesStatus.ActivateSuccess) {
            setMessageDialog(activateMessage);
            show = true;
        }
        if (status === ServicesStatus.InactivateSuccess) {
            setMessageDialog(inactivateMessage);
            show = true;
        }
        if (show) {
            setOpenMessageDialog(true);
            setTimeout(() => setOpenMessageDialog(false), 1500);
        }
    }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openMessageDialog, setOpenMessageDialog] = useState(false);
    const [messageDialog, setMessageDialog] = useState<JSX.Element>(<Fragment />);
    const [servicesToDelete, setServicesToDelete] = useState<Array<Service>>([]);

    const deleteMessage = (
        <Typography className={classes.messageDialog}>
            Services were
            <br />
            deleted successfully.
        </Typography>
    );
    const activateMessage = <Typography className={classes.messageDialog}>Services were activated.</Typography>;
    const inactivateMessage = <Typography className={classes.messageDialog}>Services were inactivated.</Typography>;

    const onDeleteHandler = (ids: Array<number>): void => {
        setServicesToDelete(
            services.filter(service => service.destroyable).filter(service => ids.includes(service.id!))
        );
        setOpenDeleteDialog(true);
    };

    const onActivateHandler = (ids: Array<number>, active: boolean): void => {
        props.activate(ids, active);
    };

    const onConfirmDeleteHandler = (): void => {
        props.delete(servicesToDelete.map(service => service.id!));
        setSelected(prev => prev.filter(selectedId => !servicesToDelete.some(s => s.id === selectedId)));
        setServicesToDelete([]);
        setOpenDeleteDialog(false);
        setShowMultiselectionBar(false);
    };

    const onSelectFilter = (statusId: string): void => {
        // Only set loading if status actually changed
        if (statusId !== filter.status) {
            setSelected([]);
            setShowMultiselectionBar(false);
            // Set loading immediately when filter changes to prevent ServicesNotCreated flash
            setLocalLoading(prev => ({ ...prev, services: true }));
            setFilter(prev => ({ ...prev, status: statusId }));
            setPage(1);
        }
    };

    const searchHandler = (searchText: string) => {
        // Only set loading if search text actually changed
        if (searchText !== filter.searchText) {
            // Set loading immediately when search changes
            setLocalLoading(prev => ({ ...prev, services: true }));
            setFilter(prev => ({ ...prev, searchText }));
            setPage(1);
        }
    };

    const onSort = (f: SortFields, newOrder: SortOrder) => {
        // Only set loading if sort actually changed
        if (f === SortFields.NAME && (f !== field || newOrder !== order)) {
            // Set loading immediately when sort changes
            setLocalLoading(prev => ({ ...prev, services: true }));
            setField(f);
            setOrder(newOrder);
            setPage(1);
        }
    };

    const clearSelector = () => {};

    const staffList: Array<StaffMember> = useSelector<RootState, Array<Staff>>(state => state.staff.staff).map(st => ({
        id: st.id!,
        firstName: st.person.firstName,
        lastName: st.person.lastName,
        avatar: st.person.avatar
    }));

    const onChangeBusinessAreaHandler = (servicesIds: Array<number>, businessAreaId: string): void => {
        props.updateBusinessArea(servicesIds, businessAreaId);
    };

    const onChangePetTypesHandler = (servicesIds: Array<number>, petTypeIds: Array<string>): void => {
        props.updatePetTypes(servicesIds, petTypeIds);
    };

    const onChangeStaffHandler = (staffIds: Array<number>): void => {
        if (selected.length === 1) {
            const [serviceId] = selected;
            dispatch(updateStaffServicesThunk(serviceId, staffIds));
        }
    };

    const onMultiselectBarCloseHandler = (): void => {
        setSelected([]);
        setShowMultiselectionBar(false);
        clearSelector();
    };

    const gridElements: Array<GridElement> = services.map(service => ({
        id: service.id!,
        name: service.name,
        selected: selected.includes(service.id!),
        statusId: service.status.id,
        duplicated: props.duplicatedServiceId?.toString() === service.uuid,
        disableActivateButton: !(
            (service.pricing.fixedPrice.length > 0 || service.pricing.variablePrice !== undefined) &&
            service.staff.length > 0
        ),
        disableDeleteButton: !service.destroyable,
        content: (
            <ServicesRowContent
                key={service.uuid}
                service={service}
                onEdit={() => props.editService(service)}
                onAddStaff={() => props.addStaff(service)}
            />
        )
    }));

    const total = meta?.totalServices ?? 0;

    // Improved logic
    const shouldShowGroomingServicesNotCreated =
        hasInitialized &&
        isTrulyEmpty &&
        filter.searchText === '' &&
        filter.status === activeOption.id &&
        !localLoading.services; // Don't show when loading filters

    const shouldShowHeader = hasEverHadServices || !shouldShowGroomingServicesNotCreated;

    const isSearchNoResults =
        hasInitialized &&
        services.length === 0 &&
        (filter.searchText !== '' || filter.status !== activeOption.id) &&
        !loading &&
        !localLoading.services; // Don't show when loading

    const shouldShowSpinner = loading || localLoading.services;

    return (
        <Box className={classes.container}>
            {/* Header conditional */}
            {shouldShowHeader && (
                <Box className={classes.headerContainer}>
                    <ServicesHeader
                        hasServices={total > 0}
                        onAddService={props.addService}
                        selectedMarketplaceIds={selectedMarketplaceIds}
                        onChangeMarketplaceIds={ids => {
                            setPage(1);
                            onChangeMarketplaceIds(ids);
                        }}
                    />
                </Box>
            )}

            {shouldShowGroomingServicesNotCreated ? (
                <Box className={classes.servicesNotCreatedContainer}>
                    <ServicesNotCreated
                        disableAddButton={false}
                        onAddService={props.addService}
                    />
                </Box>
            ) : (
                <Fragment>
                    <Box className={classes.selectorsContainer}>
                        <Search onSearch={searchHandler} />
                        <Box className={classes.selectorBarContainer}>
                            <SelectionBar
                                className={classes.selectorBarBlock}
                                options={filterOptions}
                                selectedId={filter.status}
                                onSelect={onSelectFilter}
                            />
                        </Box>
                    </Box>

                    {isSearchNoResults ? (
                        <ResultNotFound />
                    ) : (
                        <Box className={classes.gridContainer}>
                            <ServicesHeaderList onSort={onSort} />

                            {shouldShowSpinner ? (
                                <Spinner className={classes.spinner} />
                            ) : (
                                <Grid
                                    elements={gridElements}
                                    onDelete={(id: number) => onDeleteHandler([id])}
                                    onActivate={(id: number, active: boolean) => onActivateHandler([id], active)}
                                    onEdit={(serviceId: number) => {
                                        const svc = services.find(s => s.id === serviceId);
                                        if (svc) props.editService(svc);
                                    }}
                                    duplicateOptions={duplicateOptions}
                                    className={clsx(classes.grid, classes.gridServices)}
                                    mobileToolbar={true}
                                    isMobile={isMobile}
                                />
                            )}

                            {!loading && total > pageSize && (
                                <Pagination
                                    className={classes.pagination}
                                    count={total}
                                    page={page}
                                    pageSize={pageSize}
                                    onPageChange={(p: number) => {
                                        setLocalLoading(prev => ({ ...prev, services: true }));
                                        setPage(p);
                                    }}
                                    onPageSizeChange={(ps: number) => {
                                        setLocalLoading(prev => ({ ...prev, services: true }));
                                        setPageSize(ps);
                                        setPage(1);
                                    }}
                                />
                            )}
                        </Box>
                    )}
                </Fragment>
            )}

            <ConfirmDialog
                id="service_list_modal_"
                open={openDeleteDialog}
                question={
                    servicesToDelete.length === 1 ? (
                        <Box className={classes.deleteConfirmTextContainer}>
                            <Typography className={classes.deleteConfirmTextTitle}>Delete Service</Typography>
                            <Typography
                                id="services_modal_label_delete"
                                className={classes.deleteConfirmText}
                            >
                                Are you sure you want to delete
                            </Typography>
                            <Typography className={classes.deleteConfirmText}>
                                <b>{servicesToDelete[0].name}</b> service?
                            </Typography>
                        </Box>
                    ) : (
                        <Box className={classes.deleteConfirmTextContainer}>
                            <Typography className={classes.deleteConfirmText}>
                                Are you sure you want to delete
                                <br />
                                the <b>following services?</b>
                            </Typography>
                            {servicesToDelete.map(s => (
                                <Typography
                                    key={s.uuid}
                                    className={classes.deleteConfirmServices}
                                >
                                    <FontAwesomeIcon
                                        className={classes.deleteIcon}
                                        icon={faCheck}
                                    />{' '}
                                    <b>{s.name}</b>
                                </Typography>
                            ))}
                        </Box>
                    )
                }
                confirmButtonLabel="Confirm"
                cancelButtonLabel="Cancel"
                onConfirm={onConfirmDeleteHandler}
                onCancel={() => setOpenDeleteDialog(false)}
            />

            <MessageDialog
                open={openMessageDialog}
                onClose={() => setOpenMessageDialog(false)}
                message={messageDialog}
                icon={faCheckCircle}
            />

            {showMultiselectionBar && (
                <ServicesMultiselectionBar
                    selectedQty={selectedQty}
                    businessAreas={props.businessAreas}
                    petTypes={props.petTypes}
                    staff={staffList}
                    serviceStaff={
                        selectedQty === 1 ? services.find(service => selected.includes(service.id!))!.staff : []
                    }
                    onClose={onMultiselectBarCloseHandler}
                    onDelete={() => onDeleteHandler(selected)}
                    onActivate={(selectedIds, active) => onActivateHandler(selectedIds, active)}
                    onChangeBusinessArea={businessAreaId => onChangeBusinessAreaHandler(selected, businessAreaId)}
                    onChangePetTypes={petTypeIds => onChangePetTypesHandler(selected, petTypeIds)}
                    onChangeStaff={onChangeStaffHandler}
                    selectedServices={services.filter(service => selected.includes(service.id!))}
                />
            )}
        </Box>
    );
};

export default ServicesTable;
